/**
 * ControlPlaneLayerDiagram
 *
 * Layered architecture diagram showing the control plane
 * between application/infrastructure layers.
 */
import React from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { governanceTokens } from './design-tokens';

interface LayerConfig {
  label: string;
  items: string[];
}

export interface ControlPlaneLayerDiagramProps {
  topLayers: LayerConfig[];
  controlLayer: LayerConfig;
  bottomLayers: LayerConfig[];
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
  },
  layer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  layerLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: tokens.spacingVerticalXXS,
  },
  layerItems: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  layerItem: {
    fontSize: tokens.fontSizeBase200,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    whiteSpace: 'nowrap',
  },
  flowLine: {
    display: 'flex',
    justifyContent: 'center',
    padding: `${tokens.spacingVerticalXXS} 0`,
  },
  flowArrow: {
    width: '2px',
    height: '16px',
  },
});

export const ControlPlaneLayerDiagram: React.FC<ControlPlaneLayerDiagramProps> = ({
  topLayers,
  controlLayer,
  bottomLayers,
}) => {
  const styles = useStyles();

  const renderLayer = (
    layer: LayerConfig,
    variant: 'top' | 'control' | 'bottom'
  ) => {
    const bg =
      variant === 'control'
        ? `linear-gradient(135deg, ${governanceTokens.colorGovernancePrimary}, ${governanceTokens.colorGovernanceSecondary})`
        : governanceTokens.colorGovernanceCardBg;
    const textColor =
      variant === 'control' ? tokens.colorNeutralForegroundStaticInverted : governanceTokens.colorGovernanceTextSecondary;
    const itemBg =
      variant === 'control'
        // rgba(255,255,255,0.15) === the static-inverted (white) token at ~15% alpha (hex 26).
        ? `${tokens.colorNeutralForegroundStaticInverted}26`
        : governanceTokens.colorGovernanceDivider;

    return (
      <div
        className={styles.layer}
        style={{ background: bg, border: `1px solid ${governanceTokens.colorGovernanceDivider}` }}
      >
        <span className={styles.layerLabel} style={{ color: variant === 'control' ? tokens.colorNeutralForegroundStaticInverted : governanceTokens.colorGovernanceTextPrimary }}>
          {layer.label}
        </span>
        <div className={styles.layerItems}>
          {layer.items.map((item) => (
            <span
              key={item}
              className={styles.layerItem}
              style={{ backgroundColor: itemBg, color: textColor }}
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderFlowLine = (key: string) => (
    <div key={key} className={styles.flowLine}>
      <div
        className={styles.flowArrow}
        style={{ backgroundColor: governanceTokens.colorGovernancePrimary }}
      />
    </div>
  );

  return (
    <div className={styles.container}>
      {topLayers.map((layer, i) => (
        <React.Fragment key={`top-${i}`}>
          {renderLayer(layer, 'top')}
          {renderFlowLine(`flow-top-${i}`)}
        </React.Fragment>
      ))}

      {renderLayer(controlLayer, 'control')}

      {bottomLayers.map((layer, i) => (
        <React.Fragment key={`bottom-${i}`}>
          {renderFlowLine(`flow-bottom-${i}`)}
          {renderLayer(layer, 'bottom')}
        </React.Fragment>
      ))}
    </div>
  );
};
